import { inject, Injectable } from '@angular/core';
import { QuestionApiService } from '@shared/service/question-api.service';
import { SharedService } from '@shared/service/shared.service';
import { XKNodesEnum } from '@shared/model/question.models';
import { CategoryList, EchartsNode, Node } from '@shared/model/course.models';
import { BehaviorSubject } from 'rxjs';
import { CourseService } from '@shared/service/course.service';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { AssignXkwNodeComponent } from '@shared/component/assign-xkw-node/assign-xkw-node.component';
import { isNil } from 'ng-zorro-antd/core/util';
import { NzMessageService } from 'ng-zorro-antd/message';

@Injectable({
  providedIn: 'root',
})
export class AtlasManageService {
  private questionSrv = inject(QuestionApiService);
  public sharedSrv = inject(SharedService);
  private courseSrv = inject(CourseService);
  private drawerSrv = inject(NzDrawerService);
  private messageSrv = inject(NzMessageService);

  nodes: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  // 点击图表 label触发事件
  clickNodes: BehaviorSubject<Node> = new BehaviorSubject<Node>(null);
  // 改变层级事件
  changeLevel$: BehaviorSubject<{ nodes: any; level: number }> = new BehaviorSubject<{ nodes: any; level: number }>(
    null
  );
  // 更新图表事件
  updateNodes: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  // 图表展开事件
  expandNode$: BehaviorSubject<Node> = new BehaviorSubject<Node>(null);
  treeNode: Node[] = [];
  nodeList: any = null;
  metaNodes: any = null;
  // 当前选中的知识点
  currentNode: Node = null;
  // 当前选中知识点的父级节点集合
  parentNodes: Array<Node> = null;
  // 当前选中知识点的子集节点结合
  childrenNodes: Array<Node> = null;
  categoryList: CategoryList[] = [];
  // 是否展示禁用
  isDisableBtn: boolean = true;
  // 记录已展开的知识点
  expendNodeSet: Set<number> = new Set();

  selectCategory: XKNodesEnum = XKNodesEnum.TRADITION;

  get isStartV2() {
    if (this.categoryList.length === 0) return false;
    return this.categoryList.find(x => x.selected).id === XKNodesEnum.GLOBAL;
  }

  async getCategory() {
    try {
      this.categoryList = await this.courseSrv.getCategoryList(this.sharedSrv.user.subject_id);
    } catch (e) {
      this.messageSrv.error('获取版本列表失败');
    }
  }

  async getNodes() {
    await this.getCategory();
    await this.loadNodes();
  }

  async loadNodes(update: boolean = false) {
    const id = this.messageSrv.loading('获取图谱中...').messageId;
    try {
      const query = {
        subjectId: this.sharedSrv.user.subject_id,
        category: this.selectCategory,
        includeDeleted: true,
        formatMappedName: true,
      };

      if (this.selectCategory === XKNodesEnum.TRADITION) query['force'] = true;

      const result = await this.questionSrv.getXKNodes(query, false);

      this.treeNode = JSON.parse(JSON.stringify(result));

      const disabledNode = (list: EchartsNode[]) => {
        list.forEach(x => {
          if ((this.parentNodes && this.parentNodes.map(x => x.id).includes(x.id)) || this.expendNodeSet.has(x.id)) {
            x.collapsed = false;
          }
          if (x.children && x.children.length > 0) {
            if (!this.isDisableBtn) x.children = x.children.filter(x => x.isVisible);
            disabledNode(x.children);
          }
        });
      };

      disabledNode(result);

      this.metaNodes = {
        id: '1000000',
        key: 1000000,
        title: this.sharedSrv.subjectIdMap[this.sharedSrv.user.subject_id],
        children: this.treeNode,
        isVisible: true,
      };

      const node = {
        id: '1000000',
        key: 1000000,
        title: this.sharedSrv.subjectIdMap[this.sharedSrv.user.subject_id],
        children: result,
        isVisible: true,
      };

      this.nodeList = node;

      if (update) {
        this.updateNodes.next(node);
      } else {
        this.nodes.next(node);
      }
    } catch (e) {
      this.messageSrv.error(e.detail ?? '获取图谱失败');
    } finally {
      this.messageSrv.remove(id);
    }
  }

  /**
   * 找到当前节点下的子集节点。
   * @param key
   */
  findChildNode(key: number) {
    let nodes = [];
    nodes = nodes.concat(this.nodeList);
    while (nodes.length) {
      const temp = nodes.shift();
      if (temp.children) {
        nodes = nodes.concat(temp.children);
      }
      if (temp.key === key) {
        return temp.children;
      }
    }
  }

  tilNodes(node) {
    const flatNodes = [];

    const til = (nodes: Node) => {
      flatNodes.push(nodes);
      if (nodes.children) {
        nodes.children.forEach(x => {
          til(x);
        });
      }
    };

    til(node);
    return flatNodes;
  }

  findAndModifyTree(tree: Node, id: number, value: Node, type = 'edit'): void {
    if (tree.id === id) {
      if (type === 'edit') {
        tree = value;
      } else if (type === 'add') {
        tree.children.push(value);
      }
    } else if (tree.children) {
      tree.children.forEach(child => {
        this.findAndModifyTree(child, id, value);
      });
    }
  }

  /**
   * 设置子节点的可见性
   * @param node
   * @param visible
   */
  setChildrenNodesVisible(node: Node, visible: boolean) {
    node.isVisible = visible;
    // this.findAndModifyTree(this.nodeList, node.key, node, 'edit');
    if (node.children && node.children.length > 0) {
      node.children.forEach(x => {
        this.setChildrenNodesVisible(x, visible);
      });
    }
  }

  setChildrenNodesCollapsed() {
    const list = JSON.parse(JSON.stringify(this.nodeList));
    const formatData = graph => {
      if (this.parentNodes && this.parentNodes.map(x => x.id).includes(graph.id)) {
        graph.collapsed = false;
      }
      if (graph.children) {
        graph.children.forEach(x => formatData(x));
      }
    };
    formatData(list);
    return list;
  }

  /**
   * 找到当前选中的节点，所有的父级元素
   * @param list
   * @param key
   */
  findParentNode(list: any, key: number) {
    if (list.key === key) {
      return []; // 找到目标节点，返回空数组表示没有父级节点
    }

    for (const child of list.children) {
      if (child.key === key) {
        return [list]; // 找到目标节点的直接父级节点，返回只包含该父级节点的数组
      }

      const foundInChild = this.findParentNode(child, key);
      if (foundInChild.length > 0) {
        return [list, ...foundInChild]; // 找到目标节点的间接父级节点，将该父级节点和其所有父级节点添加到数组中
      }
    }

    return []; // 在当前节点及其子节点中都没有找到目标节点
  }

  changeDisabled(disabled: boolean) {
    const disabledNode = (list: EchartsNode[], disabled: boolean) => {
      list.forEach(x => {
        x.disabled = disabled;
        if (x.children && x.children.length > 0) {
          disabledNode(x.children, disabled);
        }
      });
    };
    disabledNode(this.treeNode, disabled);
  }

  /**
   * 禁用选中节点及以下。
   */
  disabledSelectKey(selectKey: number) {
    this.changeDisabled(false);
    const setDisable = (treeSelectData: any, id: any) => {
      for (let i = 0; i < treeSelectData.length; i++) {
        const node = treeSelectData[i];
        if (node.id === id) {
          // 将目标节点设置为disabled
          node.disabled = true;
          if (node.children && node.children.length > 0) {
            for (let j = 0; j < node.children.length; j++) {
              setDisable(node.children, node.children[j].id);
            }
          }
        } else {
          if (node.children && node.children.length > 0) {
            setDisable(node.children, id);
          }
        }
      }
    };

    setDisable(this.treeNode, selectKey);
  }

  assignXkwNode() {
    let ids;
    this.currentNode?.chapterNodes ? (ids = this.currentNode.chapterNodes.map(x => x.id)) : (ids = []);
    const drawer = this.drawerSrv.create({
      nzTitle: this.currentNode.title,
      nzContent: AssignXkwNodeComponent,
      nzContentParams: {
        nodeCheckedKeys: ids,
      },
    });

    drawer.afterClose.subscribe(async data => {
      if (!isNil(data)) {
        try {
          // 关联学科网图谱
          await this.courseSrv.putNodeInfo(this.sharedSrv.user.subject_id, this.currentNode.key, {
            mappedTkNodeList: data.map(x => x.id),
          });
          this.currentNode.chapterNodes = data;
          this.messageSrv.success('关联成功');
          this.loadNodes(true);
          // this.nodes.next({
          //   type: 'update',
          //   node: this.currentNode,
          // });
        } catch (e) {
          this.messageSrv.error('操作失败');
        }
      }
    });
  }
}
